import type { TBordSelectOption } from 'bord-design-system';
import { TOOL_STATUS_UI_BY_ID } from 'constants/toolInspectionStatus.constants';
import { IToolData, IToolsBulkSelectionItem, IUpdateBulkToolStateDTO } from 'types/products';

export interface IToolsBulkSelectionState {
  order: number[];
  byId: Record<number, IToolsBulkSelectionItem>;
}

export interface IToolsBulkSelectedRow extends IToolsBulkSelectionItem {
  displayName: string;
  displaySerial: string;
  statusLabel: string;
}

interface IBuildBulkToolStatusFormDataParams {
  productIds: number[];
  selectedStatusId: string;
  statusChangeReason?: string;
  statusEvidenceFile?: File;
}

/**
 * Construye las opciones enriquecidas para el selector de estado masivo.
 */
export const buildBulkToolStatusSelectOptions = (): ReadonlyArray<TBordSelectOption> => {
  return Object.entries(TOOL_STATUS_UI_BY_ID).map(([statusId, statusUi]) => ({
    value: statusId,
    label: statusUi.title,
    description: statusUi.description
  }));
};

/**
 * Construye el snapshot de una herramienta seleccionable para acciones masivas.
 */
export const buildToolsBulkSelectionItem = (toolData: IToolData): IToolsBulkSelectionItem | null => {
  if (!toolData.id) return null;

  const currentStatus = toolData?.organizationTools?.[0]?.toolStatusLog?.[0]?.toolStatus?.name;
  return {
    id: toolData.id,
    name: toolData.name,
    photoUrl: toolData.image,
    serial: toolData.serial,
    currentStatus
  };
};

/**
 * Agrega o remueve una herramienta del estado de selección masiva.
 */
export const toggleBulkSelectedToolState = (
  currentSelectionState: IToolsBulkSelectionState,
  selectionItem: IToolsBulkSelectionItem
): IToolsBulkSelectionState => {
  const { id } = selectionItem;

  if (currentSelectionState.byId[id]) {
    const { [id]: _removed, ...rest } = currentSelectionState.byId;

    return {
      byId: rest,
      order: currentSelectionState.order.filter(existingId => existingId !== id)
    };
  }

  return {
    byId: { ...currentSelectionState.byId, [id]: selectionItem },
    order: [...currentSelectionState.order, id]
  };
};

/**
 * Remueve una herramienta específica del estado de selección masiva.
 */
export const removeBulkSelectedToolStateItem = (
  currentSelectionState: IToolsBulkSelectionState,
  toolId: number
): IToolsBulkSelectionState => {
  if (!currentSelectionState.byId[toolId]) {
    return currentSelectionState;
  }

  const { [toolId]: _removed, ...rest } = currentSelectionState.byId;

  return {
    byId: rest,
    order: currentSelectionState.order.filter(existingId => existingId !== toolId)
  };
};

/**
 * Resuelve la lista ordenada de herramientas seleccionadas.
 */
export const mapBulkSelectedTools = (currentSelectionState: IToolsBulkSelectionState): IToolsBulkSelectionItem[] => {
  return currentSelectionState.order
    .map(id => currentSelectionState.byId[id])
    .filter((item): item is IToolsBulkSelectionItem => item !== undefined);
};

/**
 * Indica si una herramienta está actualmente seleccionada.
 */
export const isToolSelectedInBulkState = (
  currentSelectionState: IToolsBulkSelectionState,
  toolId?: number
): boolean => {
  if (!toolId) return false;

  return Boolean(currentSelectionState.byId[toolId]);
};

/**
 * Normaliza las herramientas seleccionadas para mostrarlas en el acordeón.
 */
export const mapToolsBulkSelectedRows = (tools: IToolsBulkSelectionItem[]): IToolsBulkSelectedRow[] => {
  return tools.map(tool => ({
    ...tool,
    displayName: tool.name || 'No disponible',
    displaySerial: tool.serial || 'No disponible',
    statusLabel: tool.currentStatus?.trim() ? tool.currentStatus : 'Desconocido'
  }));
};

/**
 * Construye el FormData requerido por el endpoint de cambio masivo de estado.
 */
export const buildBulkToolStatusFormData = ({
  productIds,
  selectedStatusId,
  statusChangeReason,
  statusEvidenceFile
}: IBuildBulkToolStatusFormDataParams): FormData => {
  const requestData: IUpdateBulkToolStateDTO = {
    productIds,
    toolStatusId: selectedStatusId
  };

  if (statusChangeReason?.trim()) {
    requestData.comments = statusChangeReason.trim();
  }

  const formData = new FormData();

  if (statusEvidenceFile) {
    formData.append('file', statusEvidenceFile);
  }

  formData.append('data', JSON.stringify(requestData));

  return formData;
};
